import { useRef, useState } from 'react';
import { Eraser } from 'lucide-react';
import { Button } from './Button';
import { Modal } from './Modal';
import './SignaturePad.css';
import { useTranslation } from 'react-i18next';

/**
 * Semnătura, desenată cu degetul sau cu mausul.
 *
 * O linie goală tipărită și semnată de mână e forma corectă pentru hârtia
 * care rămâne la dosar. Pentru un document care pleacă pe email sau care e
 * păstrat ca PDF, semnătura trebuie să fie în el — de asta pânza asta.
 *
 * Se desenează cu evenimente de pointer, aceleași pentru maus, stylus și
 * deget: recepția semnează pe o tabletă, iar un `mousedown` nu se întâmplă
 * acolo. Pânza are rezoluția ecranului (`devicePixelRatio`), altfel semnătura
 * iese cu marginile zimțate exact pe ecranele pe care se desenează cel mai des.
 *
 * Nu se poate confirma o pânză goală: un dreptunghi alb salvat ca semnătură
 * arată, pe hârtie, ca un document semnat.
 */
export function SignaturePad({
  title,
  hint,
  saving,
  onSign,
  onClose,
}: {
  title: string;
  hint?: string;
  saving?: boolean;
  /** Primește PNG-ul desenat; apelantul îl urcă unde îi trebuie. */
  onSign: (png: Blob) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [drawn, setDrawn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Pânza, la rezoluția ecranului, pregătită la prima atingere. */
  const context = () => {
    const el = canvas.current;
    if (!el) return null;
    const ctx = el.getContext('2d');
    if (!ctx) return null;
    const ratio = window.devicePixelRatio || 1;
    const want = { w: Math.round(el.clientWidth * ratio), h: Math.round(el.clientHeight * ratio) };
    if (el.width !== want.w || el.height !== want.h) {
      el.width = want.w;
      el.height = want.h;
      ctx.scale(ratio, ratio);
    }
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0a0f1e';
    return ctx;
  };

  const at = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = context();
    if (!ctx) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const p = at(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    // Un punct singur e tot o semnătură: o linie fără mișcare n-ar desena nimic.
    ctx.lineTo(p.x + 0.1, p.y);
    ctx.stroke();
    setDrawn(true);
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = context();
    if (!ctx) return;
    const p = at(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const stop = () => {
    drawing.current = false;
  };

  const clear = () => {
    const el = canvas.current;
    const ctx = el?.getContext('2d');
    if (el && ctx) ctx.clearRect(0, 0, el.width, el.height);
    setDrawn(false);
  };

  const confirm = () => {
    const el = canvas.current;
    if (!el || !drawn) return;
    el.toBlob((blob) => {
      if (!blob) {
        setError(t('semnaturaNuAPutut'));
        return;
      }
      onSign(blob);
    }, 'image/png');
  };

  return (
    <Modal title={title} onClose={onClose} width={460}>
      <div className="sgn">
        {hint ? <p className="sgn__hint">{hint}</p> : null}
        <canvas
          ref={canvas}
          className="sgn__pad"
          aria-label={t('zonaDeSemnatura')}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={stop}
          onPointerCancel={stop}
          onPointerLeave={stop}
        />
        {error ? (
          <p className="sgn__error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="sgn__actions">
          <Button onClick={clear} disabled={!drawn}>
            <Eraser size={14} /> {t('sterge')}
          </Button>
          <Button variant="primary" disabled={!drawn} loading={saving} onClick={confirm}>
            {drawn ? t('confirmaSemnatura') : t('semneazaInCaseta')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
